import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { AlertCircle, CheckCircle, Chrome, Mail, Lock, User, Users, Ticket, BarChart2, Bell, Shield } from 'lucide-react';

const Register = () => {
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get('ref')?.trim().toUpperCase() || '';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [invitationCode, setInvitationCode] = useState(refFromUrl);
  const [codeValid, setCodeValid] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);
  const [referrer, setReferrer] = useState<{
    email: string | null;
    name: string | null;
  }>({ email: null, name: null });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (invitationCode.length >= 4) {
      validateCode(invitationCode);
    } else {
      setCodeValid(false);
      setReferrer({ email: null, name: null });
    }
  }, [invitationCode]);

  const validateCode = async (code: string) => {
    setCodeChecking(true);
    try {
      const { data, error } = await supabase
        .rpc('get_referrer_details', { input_code: code });

      if (error || !data || data.length === 0) {
        setCodeValid(false);
        setReferrer({ email: null, name: null });
        return;
      }

      setCodeValid(true);
      setReferrer({
        email: data[0].referrer_email,
        name: data[0].referrer_name
      });
    } catch {
      setCodeValid(false);
      setReferrer({ email: null, name: null });
    } finally {
      setCodeChecking(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value.trim()
    });
    setError('');
  };

  const handleGoogleSignIn = async () => {
    if (!codeValid) {
      setError('Necesitas un código de invitación válido para registrarte');
      return;
    }

    try {
      setError('');
      // Guardar código en localStorage para recuperarlo después del OAuth callback
      localStorage.setItem('pending_referral_code', invitationCode);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          },
          redirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Error con Google Sign In:', err);
      setError(handleSupabaseError(err));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!codeValid) {
      setError('Necesitas un código de invitación válido para registrarte');
      return;
    }

    try {
      if (!formData.fullName) {
        setError('El nombre es obligatorio');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return;
      }

      if (formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      setLoading(true);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            referral_code: invitationCode
          },
          emailRedirectTo: `${window.location.origin}/email-confirmation`
        }
      });

      if (signUpError) throw signUpError;

      if (data?.user) {
        setSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      }
    } catch (err: any) {
      console.error('Error en el registro:', err);
      setError(handleSupabaseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-4">

        {/* Sección informativa — solo si viene con código de referido */}
        {codeValid && referrer.name && (
          <div className="bg-dark-card rounded-lg shadow-xl p-6">
            <div className="text-center mb-4">
              <p className="text-primary font-semibold text-sm">
                {referrer.name} te invita a unirte
              </p>
              <h2 className="text-xl font-bold text-white mt-1">
                Asistente Trading IA
              </h2>
              <p className="text-sm text-gray-400 mt-2">
                Herramienta de análisis institucional para Nasdaq Futures
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <BarChart2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Análisis ICT automatizado</p>
                  <p className="text-xs text-gray-400">Detecta setups con metodología validada en 11 años de datos</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Bell className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">Alertas en tiempo real</p>
                  <p className="text-xs text-gray-400">Te avisa al móvil cuando hay oportunidad — tú decides si operar</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-medium">No ejecuta por ti</p>
                  <p className="text-xs text-gray-400">Analiza el mercado y te da niveles — el control es tuyo</p>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="bg-dark-card rounded-lg shadow-xl p-8">
        <h2 className="text-2xl font-bold text-white text-center mb-2">Crear Cuenta</h2>
        <p className="text-center text-sm text-gray-400 mb-6">
          Necesitas una invitación para unirte
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-600/20 border border-red-600 rounded flex items-center text-red-100 text-sm">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {success ? (
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h3 className="text-xl font-semibold text-white">¡Registro Exitoso!</h3>
            <p className="text-gray-300">
              Te hemos enviado un email de confirmación. Por favor, revisa tu bandeja de entrada.
            </p>
            <div className="pt-4">
              <Link to="/login" className="btn-primary inline-block">
                Ir a Iniciar Sesión
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Campo de código de invitación — SIEMPRE visible y obligatorio */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Código de invitación <span className="text-primary">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value.trim().toUpperCase())}
                  placeholder="Ej: B0FF4D"
                  className={`input-field pl-10 pr-10 uppercase tracking-widest font-mono ${
                    invitationCode.length >= 4
                      ? codeValid
                        ? 'border-green-500/50'
                        : 'border-red-500/50'
                      : ''
                  }`}
                  maxLength={10}
                />
                <Ticket className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                {invitationCode.length >= 4 && !codeChecking && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {codeValid ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    )}
                  </span>
                )}
                {codeChecking && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  </span>
                )}
              </div>
              {codeValid && referrer.name && (
                <div className="mt-2 p-2 bg-green-900/20 border border-green-900/50 rounded flex items-center text-sm">
                  <Users className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                  <span className="text-green-300">
                    Invitado por: <strong>{referrer.name || referrer.email}</strong>
                  </span>
                </div>
              )}
              {invitationCode.length >= 4 && !codeValid && !codeChecking && (
                <p className="mt-1 text-xs text-red-400">
                  Código no válido. Pide el código a quien te habló de la herramienta.
                </p>
              )}
              {!invitationCode && (
                <p className="mt-1 text-xs text-gray-500">
                  Pide tu código a quien te recomendó la herramienta
                </p>
              )}
            </div>

            {/* Google Sign In — deshabilitado sin código válido */}
            <button
              onClick={handleGoogleSignIn}
              disabled={!codeValid}
              className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded transition-all duration-300 mb-6 ${
                codeValid
                  ? 'bg-white text-gray-800 hover:bg-gray-100'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Chrome className="h-5 w-5" />
              <span>Continuar con Google</span>
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-dark-card text-gray-400">O regístrate con email</span>
              </div>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-300">
                  Nombre Completo
                </label>
                <div className="mt-1 relative">
                  <input
                    type="text"
                    id="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="input-field pl-10"
                    required
                    autoComplete="name"
                  />
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Correo Electrónico
                </label>
                <div className="mt-1 relative">
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field pl-10"
                    required
                    autoComplete="email"
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Contraseña
                </label>
                <div className="mt-1 relative">
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field pl-10"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
                <p className="mt-1 text-sm text-gray-400">
                  Mínimo 6 caracteres
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                  Confirmar Contraseña
                </label>
                <div className="mt-1 relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="input-field pl-10"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !codeValid}
                className={`w-full flex items-center justify-center py-3 rounded font-medium transition-all ${
                  codeValid
                    ? 'btn-primary'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Registrando...
                  </>
                ) : (
                  'Registrarse'
                )}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-300">
              ¿Ya tienes una cuenta?{' '}
              <Link to="/login" className="text-primary hover:text-primary-hover">
                Inicia sesión aquí
              </Link>
            </p>

            <p className="mt-4 text-center text-xs text-gray-400">
              Al registrarte, aceptas nuestra{' '}
              <Link to="/privacy" className="text-primary hover:text-primary-hover">
                Política de Privacidad
              </Link>
            </p>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default Register;
